/**
 * @module lib/recommended/render
 */
import tgamConfig from "config/tgam";
import { getProp } from "utils/utils";
import { isFranchisedContent } from "utils/story";
import {
  getHeading,
  getImage,
  getLabel,
  renderSubscribedIcon,
  renderFinalImages,
  renderFinalLabels,
  renderFinalByLine
} from "./render-helper";

/**
 * @method modifiers
 * @description returns the class that will wrap all recommended articles
 * @param {String} view
 * @returns {Object}
 */
function modifiers(view) {
  const obj = {
    contentClass: "",
    linkTrackingName: "recommend-tgam",
    recommenderViewName: "Headline"
  };
  if (view === "orderedList") {
    obj.contentClass = " c-article-list__content--ordered";
  }
  if (view === "listWithImages") {
    obj.contentClass = " c-article-list__content--thumbnail";
  }
  return obj;
}

/**
 * @method wrapper
 * @description defines classes for the main parent (depending on the feature display style)
 * @param {String} style
 * @param {String} items
 * @returns {String}
 */
function wrapper(style, items) {
  let styleClass;
  let tag = (style === "orderedList" ? "ol" : (["relatedToThisStory", "articleListRow"].indexOf(style) >= 0 ? "div" : "ul"));

  if (style === "relatedToThisStory") {
    styleClass = "c-related-articles__cards";
  } else if (style === "articleListRow") {
    styleClass = "c-article-list-row__content";
  } else {
    styleClass = "c-article-list__content";
  }

  return `
    <${tag} class="${styleClass}${modifiers(style).contentClass}">
      ${items.join("")}
    </${tag}>
  `;
}

/**
 * @method normalizeArticle
 * @param {String} source
 * @param {Object} article
 * @returns {Object}
 */
function normalizeArticle(source, article) {
  let ret = {};
  if (source === "tgam") {
    const lbl = getLabel(article);
    ret = {
      id: article.content_id,
      label: lbl,
      headline: article.title,
      url: article.url,
      photo: getImage(article, lbl.text),
      byline: article.byline,
      contentType: article.content_type,
      subscribed: article.protection_product && article.protection_product === "red"
    };
  }
  return ret;
}

/**
 * @method buildClickEventData
 * @param {Object} data
 * @returns {String}
 */
function buildClickEventData(data) {
  const headline = data.label.toLowerCase().replace(/"/gm, "\"");
  const source = data.source ? data.source : "tgam";
  const contentId = data.sponsored ? `,"contentId": "${data.contentId}"` : "";
  return `{"type":"link","feature":"${data.feature.toLowerCase()}","label":"${headline}","position":${data.position},"hierarchy":1,"engine":"${source}","presentation":"${data.style}"${contentId}}`;
}

/**
 * @method renderOrderedListPackage
 * @param {Object} data
 * @returns {String}
 */
function renderOrderedListPackage(data) {
  const items = data.items.map((item, index) => {
    const displayData = normalizeArticle(data.source, item);
    const clickEventData = buildClickEventData({
      feature: data.heading,
      contentId: displayData.id,
      label: displayData.headline,
      page: `${tgamConfig.linkTracking.position}:${data.heading}:${modifiers(data.style).linkTrackingName}`,
      position: index + 1,
      style: data.style
    });
    const franchisedContentClass = isFranchisedContent(displayData.url) ? " c-card--franchised" : "";
    return `
      <div class="u-wrapper">
      <div class="c-card c-card--transparent${franchisedContentClass}">
        <a href="${tgamConfig.env.baseUrl}${displayData.url}" class="c-card__grid c-card__link"
          data-sophi="${displayData.id}"
          data-analyticsclick='${clickEventData}'
          data-recommended-request-id="${data.requestId}"
          data-recommended-view="${modifiers(data.style).recommenderViewName}"
          data-lt-pos="${tgamConfig.linkTracking.position}:${data.heading}:${modifiers(data.style).linkTrackingName}"
          data-lt-lid="Headline.${index + 1}">
          <div class="c-card__content">
            <span class="c-card__hed">
              <span class="c-card__hed-text">${displayData.headline}</span>${displayData.subscribed ? renderSubscribedIcon() : ""}
            </span>
          </div>
        </a>
      </div>
      </div>`;
  });
  const heading = data.heading ? `<span class="c-package__label c-package__label--transparent">${data.heading}</span>` : "";
  return `
    <div class="c-package c-package--story-list c-package--story-list--numbered">
      ${heading}
      <div class="c-package__stories">
        ${items.join("")}
      </div>
    </div>`;
}

/**
 * @method render
 * @description renders a view of recommended articles
 * @param {Object} data
 * @returns {String}
 */
export default function render(data = {}) {
  if (!data.items || !data.style || !data.heading || !data.source) {
    return "";
  }
  if (data.style === "orderedListPackage") {
    return renderOrderedListPackage(data);
  }
  const items = data.items.map((item, index) => {
    const displayData = normalizeArticle(data.source, item);
    const img = renderFinalImages(data, displayData);
    const label = renderFinalLabels(data, displayData);
    const byline = renderFinalByLine(displayData);
    const subscribed = displayData.subscribed ? renderSubscribedIcon() : "";
    const requestId = getProp(data, "requestId", "");
    const relatedStory = (data.style === "relatedToThisStory") ? true : false;

    // ad sequencing content
    const sponsoredCustomContent = item.hasOwnProperty("sponsored") && item.sponsored === true;
    const clickEventData = buildClickEventData({
      feature: data.heading,
      contentId: displayData.id,
      label: displayData.headline,
      page: `${tgamConfig.linkTracking.position}:${data.heading}:${modifiers(data.style).linkTrackingName}`,
      position: index + 1,
      style: data.style,
      sponsored: sponsoredCustomContent
    });
    const isFranchised = isFranchisedContent(displayData.url);
    let franchisedContentClass = "";

    if (sponsoredCustomContent) {
      franchisedContentClass = isFranchised ? " c-article-list__item--franchised" : "";

      return `<li class="c-article-list__item c-article-list__item--sponsored u-always-bleed${franchisedContentClass}">
        <div class="c-article-list__item-content">
          <a href="${tgamConfig.env.baseUrl}${displayData.url}?intcmp=${data.heading.replace(/\s/g, "-").toLowerCase()}" class="c-article-list__link" data-analyticsclick='${clickEventData}' rel="nofollow" data-sponsored="true">
            <span class="c-article-list__icon-text-wrapper">
              <span class="c-article-list__link-label" style="font-size: 0.825rem">Sponsor Content</span>
              <span class="c-article-list__link-text" style="font-size: 1.0625rem;">${displayData.headline}</span>
            </span>
            <div class="c-article-list__img-wrapper">
              <img alt="Photo" class="c-article-list__img" src="${displayData.photo.path}">
            </div>
          </a>
          </div>
      </li>`;
    } else if (data.style === "articleListRow") {
      franchisedContentClass = isFranchised ? " c-card--franchised" : "";

      return `
      <div class="c-card${franchisedContentClass}">
        <a class="c-card__grid c-card__link"
          data-sophi="${displayData.id}"
          data-analyticsclick='${clickEventData}'
          data-recommended-request-id="${requestId}"
          data-recommended-view="${modifiers(data.style).recommenderViewName}"
          data-lt-pos="${tgamConfig.linkTracking.position}:${data.heading}:${modifiers(data.style).linkTrackingName}"
          data-lt-lid="Headline.${index + 1}"
          href="${tgamConfig.env.baseUrl}${displayData.url}">
            <div class="c-card__content">
              ${label}
              <div class="c-card__hed">
                <div class="c-card__hed-text">${displayData.headline} ${subscribed}</div>
              </div>
              ${byline}
            </div>
            <div class="c-card__media">
              ${img}
            </div>
        </a>
      </div>`;
    } else {
      let openingTag = relatedStory === true ? "<div class='c-card'>" : "<li class='c-article-list__item'>";
      if (isFranchised) {
        openingTag = relatedStory === true ? "<div class='c-card c-card--franchised'>" : "<li class='c-article-list__item c-article-list__item--franchised'>";
      }
      return `${openingTag}
          ${relatedStory === false ? "<div class='c-article-list__item-content'>" : ""}
            <a class="c-article-list__link js-c-article-list__link"
              data-sophi="${displayData.id}"
              data-analyticsclick='${clickEventData}'
              data-recommended-request-id="${requestId}"
              data-recommended-view="${modifiers(data.style).recommenderViewName}"
              data-lt-pos="${tgamConfig.linkTracking.position}:${data.heading}:${modifiers(data.style).linkTrackingName}"
              data-lt-lid="Headline.${index + 1}"
              href="${tgamConfig.env.baseUrl}${displayData.url}">
                <span class="c-article-list__icon-text-wrapper">
                  ${relatedStory === false ? label : ""}
                  <span class="c-article-list__link-text">${displayData.headline} ${subscribed}</span>
                  ${byline}
                </span>
                ${img}
            </a>
          ${relatedStory === false ? "</div>" : ""}
        ${relatedStory === true ? "</div>" : "</li>"}
        `;
    }
  });

  return `
    <div class='${data.style === "articleListRow" ? "c-article-list-row" : "c-article-list"}' aria-live="polite" data-content-dist="recommended">
      ${getHeading(data.heading, data.style)}
      ${wrapper(data.style, items)}
    </div>`;
}
